import React, { useContext, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { AutomationEditorContext } from '../AutomationEditorContext';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import { StepDialogFooter } from '../StepDialogFooter';
import { SendMessageForm } from './SendMessageForm';
import type {
  SendMessageFormField,
  SenderDetailsUpdate,
  SendMessageAction,
  SenderLocalContents,
  AIGeneratedCommunicationData,
} from './types';
import { AIEmailGeneratorDialog } from './ai-email-generator';
import { StripoDialog } from './stripo';
import { useTemplateMergeFields } from './useTemplateMergeFields';
import styles from './SendMessageStep.module.css';
import { usePartnerProfile } from '@/hooks/usePartnerSettings';

function buildFormData(
  action: SendMessageAction,
  senderProfileId: SenderLocalContents['senderProfileId'],
  usePointOfContactSender: SenderLocalContents['usePointOfContactSender'],
) {
  return {
    subject: action.subject || '',
    previewText: action.previewText || '',
    html: action.html || '',
    css: action.css || '',
    compiledHtml: action.compiledHtml || '',
    text: action.text || '',
    senderProfileId: senderProfileId ?? null,
    usePointOfContactSender: !!usePointOfContactSender,
  };
}

interface SendMessageDialogProps {
  onClose: () => void;
  action: SendMessageAction;
  actionIndex: number;
}

export const SendMessageDialog: React.FC<SendMessageDialogProps> = ({
  onClose,
  action,
  actionIndex,
}) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { updateAction, updatePointOfContactSender, closeStripo, stripoIsOpen } = editor;
  const {
    senderProfileId: savedSenderProfileId,
    usePointOfContactSender: savedUsePointOfContactSender,
  } = editor.state.localContents;
  const { partner } = usePartnerProfile();
  const { mergeFields } = useTemplateMergeFields();

  const [formData, setFormData] = useState(
    buildFormData(action, savedSenderProfileId, savedUsePointOfContactSender),
  );

  const stripoSeedHtml = formData.html || partner?.emailTemplateOverride?.html || '';
  const stripoSeedCss = formData.css || partner?.emailTemplateOverride?.css || '';
  const [aiDialogIsOpen, setAiDialogIsOpen] = useState(false);

  const handleOK = () => {
    const { senderProfileId, usePointOfContactSender, ...actionData } = formData;

    updateAction(actionIndex, {
      subject: actionData.subject,
      previewText: actionData.previewText,
      html: actionData.html,
      css: actionData.css,
      compiledHtml: actionData.compiledHtml,
      text: actionData.text,
    });
    updatePointOfContactSender({ senderProfileId, usePointOfContactSender });
    onClose();
  };

  const setFormFieldValue = (field: SendMessageFormField, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openAI = () => {
    const hasExistingEmail = !!formData.html?.trim();
    if (hasExistingEmail) {
      const shouldReplace = window.confirm(
        'This will replace your existing email template, subject line, and preview text. Are you sure?',
      );
      if (!shouldReplace) return;
    }
    setAiDialogIsOpen(true);
  };
  const closeAI = () => setAiDialogIsOpen(false);

  const handleStripoSave = (newHtml: string, newCss: string, compiledHtml: string) => {
    setFormData(prev => ({
      ...prev,
      html: newHtml,
      css: newCss,
      compiledHtml,
    }));
    closeStripo();
  };

  const handleAIGenerated = ({ smsText, ...rest }: AIGeneratedCommunicationData) => {
    setFormData(prev => ({ ...prev, ...rest, text: smsText }));
  };

  const handleSenderProfileChange = (update: SenderDetailsUpdate) => {
    setFormData(prev => ({
      ...prev,
      senderProfileId: update.senderProfileId,
      usePointOfContactSender: update.usePointOfContactSender,
    }));
  };

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.dialog} open>
        <header className={styles.dialogHeader}>
          <div className={styles.dialogHeaderCopy}>
            <h2>Configure Send Message</h2>
            <p>Compose your message content</p>
          </div>
          <div className={styles.dialogHeaderActions}>
            <button type="button" className={styles.aiButton} onClick={openAI}>
              <Sparkles />
              Build with AI
            </button>
            <button type="button" className={styles.closeButton} onClick={onClose}>
              <X />
            </button>
          </div>
        </header>

        <main>
          <SendMessageForm
            subject={formData.subject}
            previewText={formData.previewText}
            html={formData.html}
            css={formData.css}
            text={formData.text}
            onChange={setFormFieldValue}
            senderProfileId={formData.senderProfileId}
            usePointOfContactSender={formData.usePointOfContactSender}
            onSenderProfileChange={handleSenderProfileChange}
          />
        </main>

        <StepDialogFooter onCancel={onClose} onOK={handleOK} />
      </dialog>

      {stripoIsOpen && (
        <StripoDialog
          onClose={closeStripo}
          html={stripoSeedHtml}
          css={stripoSeedCss}
          onSave={handleStripoSave}
          mergeFields={mergeFields}
        />
      )}
      {aiDialogIsOpen && <AIEmailGeneratorDialog onClose={closeAI} onGenerated={handleAIGenerated} />}
    </>
  );
};
